// @ts-nocheck
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { toast } from "sonner"
import { useLocation } from "react-router-dom"
import { persistStore } from "src/persist-store"
import { PATH_ROOT } from "@/routes/paths"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import { useDemoDealsDocuments } from "@/hooks/deals/useDemoDealsDocuments"
import { useRealDealsDocuments } from "@/hooks/deals/useRealDealsDocuments"
import {
  ChatScope,
  createCopyAction,
  createNavigateAction,
  createRatingAction,
  type ChatContextItem,
  type ChatDockAnchor,
  type ChatLayoutMode,
  type ChatPanelSize,
  type ChatProgressFrame,
  type ChatSession,
  type Message,
} from "@/types/chat.types"
import {
  getPageContextItem,
  type ContextItem as SelectorContextItem,
} from "@/components/ui/chat-context-selector"
import {
  sendChatMessageApi,
  listChatMessagesApi,
  type WebChatMessage,
} from "src/api/chat/Chat"
import { useChatSocket, type UseChatSocketOptions } from "src/hooks/chat/useChatSocket"
import { useChatSessions } from "src/hooks/chat/useChatSessions"
import { useChatMessages } from "src/hooks/chat/useChatMessages"

export type ChatPreset = "simple" | "detailed" | "developer"

export interface ChatSettings {
  preset: ChatPreset
  showTimestamps: boolean
  showModelInfo: boolean
  showTokenUsage: boolean
  showScopeBadge: boolean
  showContextSent: boolean
  showConfidence: boolean
  autoPageContext: boolean
  defaultScope: ChatScope
  fontSize: "small" | "medium" | "large"
}

const PRESET_SETTINGS: Record<ChatPreset, Omit<ChatSettings, "preset">> = {
  simple: {
    showTimestamps: false,
    showModelInfo: false,
    showTokenUsage: false,
    showScopeBadge: false,
    showContextSent: false,
    showConfidence: false,
    autoPageContext: true,
    defaultScope: ChatScope.Global,
    fontSize: "medium",
  },
  detailed: {
    showTimestamps: true,
    showModelInfo: true,
    showTokenUsage: false,
    showScopeBadge: true,
    showContextSent: true,
    showConfidence: false,
    autoPageContext: true,
    defaultScope: ChatScope.Global,
    fontSize: "medium",
  },
  developer: {
    showTimestamps: true,
    showModelInfo: true,
    showTokenUsage: true,
    showScopeBadge: true,
    showContextSent: true,
    showConfidence: true,
    autoPageContext: true,
    defaultScope: ChatScope.Global,
    fontSize: "medium",
  },
}

const DEFAULT_SETTINGS: ChatSettings = {
  preset: "simple",
  ...PRESET_SETTINGS.simple,
}

const DEFAULT_LAYOUT = {
  mode: "dock" as ChatLayoutMode,
  dockAnchor: "center" as ChatDockAnchor,
  panelSize: "medium" as ChatPanelSize,
}

const PREFS_STORAGE_KEY = "app-chat-prefs-v1"

interface PersistedChatPrefs {
  settings: ChatSettings
  layout: {
    mode: ChatLayoutMode
    dockAnchor: ChatDockAnchor
    panelSize: ChatPanelSize
  }
  currentSessionId?: string
}

interface ChatPanelContextType {
  sessions: ChatSession[]
  currentSessionId: string
  currentSession: ChatSession
  messages: Message[]
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  openPanel: () => void
  closePanel: () => void
  togglePanel: () => void
  isCurrentSessionLoading: boolean
  settings: ChatSettings
  updateSettings: (patch: Partial<ChatSettings>) => void
  applyPreset: (preset: ChatPreset) => void
  layoutMode: ChatLayoutMode
  setLayoutMode: (mode: ChatLayoutMode) => void
  dockAnchor: ChatDockAnchor
  setDockAnchor: (anchor: ChatDockAnchor) => void
  panelSize: ChatPanelSize
  setPanelSize: (size: ChatPanelSize) => void
  togglePanelSize: () => void
  currentDraft: string
  setCurrentDraft: (draft: string) => void
  currentScope: ChatScope
  setCurrentScope: (scope: ChatScope) => void
  contextItems: ChatContextItem[]
  setContextItems: (items: ChatContextItem[]) => void
  removeContextItem: (id: string) => void
  dynamicPageItems: ChatContextItem[]
  createSession: (scope?: ChatScope) => string
  selectSession: (sessionId: string) => void
  deleteSession: (sessionId: string) => void
  sendMessage: (overrideText?: string, options?: { scope?: ChatScope }) => void
  progressFrames: ChatProgressFrame[]
}

const ChatPanelContext = createContext<ChatPanelContextType | undefined>(undefined)

function createSessionId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function createInitialSession(scope: ChatScope = ChatScope.Global): ChatSession {
  const now = new Date()

  return {
    id: createSessionId(),
    title: "New Chat",
    messages: [],
    scope,
    createdAt: now,
    updatedAt: now,
    draft: "",
    contextItems: [],
  }
}

function generateSessionSummary(session: ChatSession): string {
  if (session.summary) {
    return session.summary
  }

  const firstUserMessage = session.messages.find((message) => message.role === "user")
  if (!firstUserMessage) {
    return "New conversation"
  }

  const preview = firstUserMessage.content.slice(0, 80)
  return preview.length < firstUserMessage.content.length ? `${preview}...` : preview
}

function normaliseContextItem(item: SelectorContextItem | ChatContextItem): ChatContextItem {
  const detail = "detail" in item ? item.detail : undefined
  const scope = "scope" in item ? item.scope : undefined

  return {
    id: item.id,
    type: item.type,
    label: item.label,
    value: item.value,
    description: item.description ?? detail,
    detail: detail ?? item.description,
    scope,
  }
}

function resolveCurrentPageItem(
  pathname: string,
  dynamicPageItems: ChatContextItem[],
): ChatContextItem | null {
  const staticItem = getPageContextItem(pathname)
  if (staticItem) {
    return normaliseContextItem(staticItem)
  }

  const normalised = pathname.replace(/^\/demo/, "")
  const dealMatch = normalised.match(/^\/deal\/(.+)$/)
  if (dealMatch) {
    return (
      dynamicPageItems.find((item) => item.value === `/deal/${dealMatch[1]}`) ?? {
        id: `page-deal-${dealMatch[1]}`,
        type: "page",
        label: "Deal",
        value: `/deal/${dealMatch[1]}`,
        description: "Deal page",
      }
    )
  }

  const analysisMatch = normalised.match(/^\/analysis\/(.+)$/)
  if (analysisMatch) {
    return (
      dynamicPageItems.find((item) => item.value === `/analysis/${analysisMatch[1]}`) ?? {
        id: `page-analysis-${analysisMatch[1]}`,
        type: "page",
        label: "Analysis",
        value: `/analysis/${analysisMatch[1]}`,
        description: "Contract analysis",
      }
    )
  }

  return null
}

function parseCurrencyValue(value: string) {
  const parsed = Number.parseInt(value.replace(/[$,]/g, ""), 10)
  return Number.isFinite(parsed) ? parsed : 0
}

export { PRESET_SETTINGS }

export const useChatPanel = () => {
  const context = useContext(ChatPanelContext)
  if (!context) {
    throw new Error("useChatPanel must be used within a ChatPanelProvider")
  }
  return context
}

interface ChatPanelProviderProps {
  children: ReactNode
  socketHook?: (options: UseChatSocketOptions) => void
  sendApi?: (req: import("src/api/chat/Chat").SendChatMessageRequest) => Promise<import("src/api/chat/Chat").SendChatMessageResponse>
}

export const ChatPanelProvider: React.FC<ChatPanelProviderProps> = ({ children, socketHook, sendApi }) => {
  const location = useLocation()
  const isDemo = useIsDemo()
  const {
    demoDeals,
    demoContracts: demoContractsData,
    demoContractIds,
  } = useDemoDealsDocuments()
  // Real-mode context chips: the user's actual deals + completed analyses.
  // Route-authoritative — empty in demo mode, so it never bleeds demo data
  // and never offers demo docs as context on the real chat. (Lane 6)
  const { realDealItems } = useRealDealsDocuments()

  const initialStateRef = React.useRef<{ sessions: ChatSession[]; currentSessionId: string } | null>(null)
  if (!initialStateRef.current) {
    const initialSession = createInitialSession()
    initialStateRef.current = {
      sessions: [initialSession],
      currentSessionId: initialSession.id,
    }
  }

  const [sessions, setSessions] = useState<ChatSession[]>(initialStateRef.current.sessions)
  const [currentSessionId, setCurrentSessionId] = useState<string>(initialStateRef.current.currentSessionId)
  const [isOpen, setIsOpen] = useState(false)
  const [loadingSessionIds, setLoadingSessionIds] = useState<string[]>([])
  const [settings, setSettings] = useState<ChatSettings>(DEFAULT_SETTINGS)
  const [layoutMode, setLayoutModeState] = useState<ChatLayoutMode>(DEFAULT_LAYOUT.mode)
  const [dockAnchor, setDockAnchorState] = useState<ChatDockAnchor>(DEFAULT_LAYOUT.dockAnchor)
  const [panelSize, setPanelSizeState] = useState<ChatPanelSize>(DEFAULT_LAYOUT.panelSize)
  const [hasHydratedPrefs, setHasHydratedPrefs] = useState(false)
  const [progressFrames, setProgressFrames] = useState<ChatProgressFrame[]>([])

  // Demo and real diverge here: in demo mode the chips come from the canned
  // demo deals/contracts; on the real (route-authoritative) path they come
  // from the user's real deals + completed analyses via useRealDealsDocuments.
  // Previously BOTH modes read demoDeals/demoContractsData, so the real Ari
  // chat offered DEMO documents as context. (Lane 6)
  const dynamicPageItems = useMemo<ChatContextItem[]>(() => {
    if (!isDemo) {
      return realDealItems
    }
    return [
      ...demoDeals.map((deal) => ({
        id: `page-deal-${deal.id}`,
        type: "page" as const,
        label: deal.name,
        value: `/deal/${deal.id}`,
        description: `${deal.type} · ${deal.value}`,
      })),
      ...demoContractsData.map((contract) => ({
        id: `page-analysis-${contract.contractId}`,
        type: "page" as const,
        label: contract.name,
        value: `/analysis/${contract.contractId}`,
        description: `Analysis · ${contract.type}`,
      })),
    ]
  }, [demoContractsData, demoDeals, isDemo, realDealItems])

  const currentSession = useMemo(() => {
    const found = sessions.find((session) => session.id === currentSessionId)
    if (found) return found
    // ID doesn't match any session — sync state to first session
    if (sessions[0] && sessions[0].id !== currentSessionId) {
      queueMicrotask(() => setCurrentSessionId(sessions[0].id))
    }
    return sessions[0]
  }, [currentSessionId, sessions])

  const updateSession = useCallback((sessionId: string, updater: (session: ChatSession) => ChatSession) => {
    setSessions((previousSessions) =>
      previousSessions.map((session) => (
        session.id === sessionId ? updater(session) : session
      ))
    )
  }, [])

  useEffect(() => {
    let isMounted = true

    const loadPrefs = async () => {
      try {
        const storedPrefs = await persistStore.getLocal<PersistedChatPrefs>(PREFS_STORAGE_KEY)
        if (!isMounted || !storedPrefs) {
          return
        }

        setSettings({ ...DEFAULT_SETTINGS, ...storedPrefs.settings })
        setLayoutModeState(storedPrefs.layout.mode)
        setDockAnchorState(storedPrefs.layout.dockAnchor)
        setPanelSizeState(storedPrefs.layout.panelSize)
        if (storedPrefs.currentSessionId) {
          setCurrentSessionId(storedPrefs.currentSessionId)
        }
      } catch (error) {
        console.error("Error loading chat preferences:", error)
      } finally {
        if (isMounted) {
          setHasHydratedPrefs(true)
        }
      }
    }

    loadPrefs()

    return () => {
      isMounted = false
    }
  }, [])

  // ─── SWR: server-side session list ──────────────────────────────────
  // Deduplicated, cached, survives page refresh via persist-store.
  // Merges with local-only sessions (not yet persisted on server).
  // Skip when a custom socketHook is provided (test/mock mode).
  const { sessions: serverSessions } = useChatSessions(!socketHook)

  useEffect(() => {
    if (!serverSessions || serverSessions.length === 0) return
    setSessions((current) => {
      const existingIds = new Set(current.map((s) => s.id))
      const persisted: ChatSession[] = serverSessions
        .filter((s) => !existingIds.has(s.id))
        .map((s) => ({
          id: s.id,
          title: s.title,
          messages: [],
          scope: ChatScope.Global,
          createdAt: new Date(s.firstAt),
          updatedAt: new Date(s.lastAt),
          draft: "",
          contextItems: [],
        }))
      if (persisted.length === 0) return current
      return [...persisted, ...current]
    })
  }, [serverSessions])

  // ─── SWR: message hydration for current session ─────────────────────
  // Cached + persisted via SWR cache provider. Switching sessions = new
  // key = instant from cache or single fetch. No manual dedup refs needed.
  const shouldFetchMessages = !isDemo && !socketHook && !!currentSession && currentSession.messages.length === 0
  const { messages: hydratedMessages } = useChatMessages(
    shouldFetchMessages ? currentSessionId : null,
  )

  useEffect(() => {
    if (!hydratedMessages || hydratedMessages.length === 0) return
    if (!currentSession || currentSession.messages.length > 0) return
    updateSession(currentSessionId, (session) => ({
      ...session,
      messages: hydratedMessages,
    }))
  }, [hydratedMessages, currentSessionId, currentSession, updateSession])

  // ─── SSE subscription for real-time assistant frames ──────────────
  // The SSE payload is a lightweight notification (no bodyText). When an
  // outbound message frame arrives, fetch the full message via REST.
  const handleSseFrame = useCallback(async (frame: WebChatMessage) => {
    if (frame.direction !== "outbound") return

    // Progress frames: typing, tool_start, tool_done — ephemeral state
    if (frame.kind === "typing" || frame.kind === "tool_start" || frame.kind === "tool_done") {
      const toolEvent = frame.toolEvents as { toolName?: string; toolLabel?: string; durationMs?: number; phase?: string; label?: string } | null
      if (frame.kind === "typing") {
        setProgressFrames((prev) => [...prev, {
          kind: "typing",
          thinkingState: {
            phase: (toolEvent?.phase as "reasoning" | "planning" | "summarizing" | "tool_routing") ?? "reasoning",
            label: toolEvent?.label ?? "Thinking...",
          },
          timestamp: new Date(frame.createdAt),
        }])
      } else if (frame.kind === "tool_start") {
        setProgressFrames((prev) => [...prev, {
          kind: "tool_start",
          toolProgress: {
            toolName: toolEvent?.toolName ?? "unknown",
            toolLabel: toolEvent?.toolLabel ?? "Working...",
          },
          timestamp: new Date(frame.createdAt),
        }])
      } else {
        setProgressFrames((prev) => [...prev, {
          kind: "tool_done",
          toolProgress: {
            toolName: toolEvent?.toolName ?? "unknown",
            toolLabel: toolEvent?.toolLabel ?? "Done",
            durationMs: toolEvent?.durationMs,
          },
          timestamp: new Date(frame.createdAt),
        }])
      }
      return
    }

    if (frame.kind !== "message") return

    // Message frame — clear progress and process normally
    setProgressFrames([])
    const sessionId = frame.chatSessionId

    // If the frame itself has bodyText (mock controller), use it directly
    if (frame.bodyText) {
      updateSession(sessionId, (session) => {
        if (session.messages.some((m) => m.id === frame.webChatMessageId)) return session
        const newMessage: Message = {
          id: frame.webChatMessageId,
          role: "assistant",
          content: frame.bodyText!,
          timestamp: new Date(frame.createdAt),
        }
        return {
          ...session,
          summary: generateSessionSummary({ ...session, messages: [...session.messages, newMessage] }),
          messages: [...session.messages, newMessage],
          updatedAt: new Date(),
        }
      })
      setLoadingSessionIds((ids) => ids.filter((id) => id !== sessionId))
      return
    }

    // Fetch the full message content from the server
    try {
      const result = await listChatMessagesApi({
        chatSessionId: sessionId,
        afterIso: new Date(Date.now() - 30_000).toISOString(),
        limit: 10,
      })

      const fullMessage = result.messages.find(
        (m) => m.webChatMessageId === frame.webChatMessageId
      ) ?? result.messages.find(
        (m) => m.direction === "outbound" && m.kind === "message" && m.bodyText
      )

      if (!fullMessage?.bodyText) return

      updateSession(sessionId, (session) => {
        if (session.messages.some((m) => m.id === fullMessage.webChatMessageId)) return session
        const newMessage: Message = {
          id: fullMessage.webChatMessageId,
          role: "assistant",
          content: fullMessage.bodyText ?? "",
          timestamp: new Date(fullMessage.createdAt),
        }
        return {
          ...session,
          summary: generateSessionSummary({ ...session, messages: [...session.messages, newMessage] }),
          messages: [...session.messages, newMessage],
          updatedAt: new Date(),
        }
      })
      setLoadingSessionIds((ids) => ids.filter((id) => id !== sessionId))
    } catch (err) {
      console.warn("[ChatPanelContext] failed to fetch message after SSE frame", err)
    }
  }, [updateSession])

  // Connect SSE once the user has sent at least one message in this session
  // (server needs a web_chat_messages row to pass the participant check).
  // Stays connected while the panel is open — no open/close per message.
  // pg_notify is server-agnostic so any ECS instance can receive the frame.
  const sessionHasMessages = !!(currentSession && currentSession.messages.length > 0)
  const useSocket = socketHook ?? useChatSocket
  useSocket({
    chatSessionId: (!isDemo && sessionHasMessages) ? currentSession!.id : null,
    onFrame: handleSseFrame,
    enabled: isOpen,
  })

  useEffect(() => {
    if (!hasHydratedPrefs) {
      return
    }

    persistStore.putLocal<PersistedChatPrefs>(
      PREFS_STORAGE_KEY,
      {
        settings,
        layout: {
          mode: layoutMode,
          dockAnchor,
          panelSize,
        },
        currentSessionId,
      },
      { persistOnLogout: true },
    ).catch((error) => {
      console.error("Error saving chat preferences:", error)
    })
  }, [currentSessionId, dockAnchor, hasHydratedPrefs, layoutMode, panelSize, settings])

  useEffect(() => {
    if (!currentSession) {
      return
    }

    if (!settings.autoPageContext) {
      updateSession(currentSession.id, (session) => ({
        ...session,
        contextItems: session.contextItems.filter((item) => item.type !== "page"),
      }))
      return
    }

    const pageItem = resolveCurrentPageItem(location.pathname, dynamicPageItems)
    if (!pageItem) {
      return
    }

    updateSession(currentSession.id, (session) => {
      const withoutPageItems = session.contextItems.filter((item) => item.type !== "page")
      return {
        ...session,
        contextItems: [pageItem, ...withoutPageItems],
      }
    })
  }, [currentSession?.id, dynamicPageItems, location.pathname, settings.autoPageContext, updateSession])

  const openPanel = useCallback(() => setIsOpen(true), [])
  const closePanel = useCallback(() => setIsOpen(false), [])
  const setPanelOpen = useCallback((open: boolean) => setIsOpen(open), [])
  const togglePanel = useCallback(() => setIsOpen((currentOpen) => !currentOpen), [])

  const updateSettings = useCallback((patch: Partial<ChatSettings>) => {
    setSettings((currentSettings) => {
      const nextSettings = {
        ...currentSettings,
        ...patch,
        preset: patch.preset ?? "simple",
      }

      return nextSettings
    })
  }, [])

  const applyPreset = useCallback((preset: ChatPreset) => {
    setSettings({
      preset,
      ...PRESET_SETTINGS[preset],
    })
  }, [])

  const setLayoutMode = useCallback((mode: ChatLayoutMode) => {
    setLayoutModeState(mode)
    setIsOpen(true)
  }, [])

  const setDockAnchor = useCallback((anchor: ChatDockAnchor) => {
    setDockAnchorState(anchor)
  }, [])

  const setPanelSize = useCallback((size: ChatPanelSize) => {
    setPanelSizeState(size)
  }, [])

  const togglePanelSize = useCallback(() => {
    setPanelSizeState((currentSize) => currentSize === "medium" ? "large" : "medium")
  }, [])

  const setCurrentDraft = useCallback((draft: string) => {
    if (!currentSession) {
      return
    }

    updateSession(currentSession.id, (session) => ({
      ...session,
      draft,
    }))
  }, [currentSession, updateSession])

  const setCurrentScope = useCallback((scope: ChatScope) => {
    if (!currentSession) {
      return
    }

    updateSession(currentSession.id, (session) => ({
      ...session,
      scope,
    }))
  }, [currentSession, updateSession])

  const setContextItems = useCallback((items: ChatContextItem[]) => {
    if (!currentSession) {
      return
    }

    updateSession(currentSession.id, (session) => ({
      ...session,
      contextItems: items.map(normaliseContextItem),
    }))
  }, [currentSession, updateSession])

  const removeContextItem = useCallback((id: string) => {
    if (!currentSession) {
      return
    }

    updateSession(currentSession.id, (session) => ({
      ...session,
      contextItems: session.contextItems.filter((item) => item.id !== id),
    }))
  }, [currentSession, updateSession])

  const createSession = useCallback((scope?: ChatScope) => {
    const nextSession = createInitialSession(scope ?? settings.defaultScope)
    setSessions((previousSessions) => [nextSession, ...previousSessions])
    setCurrentSessionId(nextSession.id)
    return nextSession.id
  }, [settings.defaultScope])

  const selectSession = useCallback((sessionId: string) => {
    setCurrentSessionId(sessionId)
  }, [])

  const deleteSession = useCallback((sessionId: string) => {
    setSessions((previousSessions) => {
      const remainingSessions = previousSessions.filter((session) => session.id !== sessionId)
      const fallbackSession = remainingSessions[0] ?? createInitialSession(settings.defaultScope)
      const nextSessions = remainingSessions.length > 0 ? remainingSessions : [fallbackSession]

      setCurrentSessionId((activeSessionId) => (
        activeSessionId === sessionId ? nextSessions[0].id : activeSessionId
      ))

      return nextSessions
    })
  }, [settings.defaultScope])

  const buildMockResponse = useCallback((
    userInput: string,
    messageId: string,
    scope: ChatScope,
    contextItems: ChatContextItem[],
  ): Message => {
    const paths = isDemo ? PATH_ROOT.demo : PATH_ROOT.main

    if (contextItems.length > 0) {
      const firstItem = contextItems[0]

      if (firstItem.type === "contract" || firstItem.type === "document") {
        const contract = demoContractsData.find((item) => item.contractId === firstItem.id)
        if (contract) {
          const responseContent = [
            `Analyzing "${contract.name}" (${contract.type}).`,
            `Status: ${contract.status}.`,
            `Shared with ${contract.sharedWith.length} collaborator(s).`,
            "I can break down key clauses, flag risks, or compare it against another agreement.",
          ].join(" ")

          return {
            id: messageId,
            role: "assistant",
            content: responseContent,
            timestamp: new Date(),
            scope,
            actions: [
              createNavigateAction("View Analysis", `${paths.analysis}/${contract.contractId}`),
              createRatingAction(messageId),
              createCopyAction(responseContent),
            ],
          }
        }
      }

      if (firstItem.type === "page") {
        const responseContent = [
          `I can help with the "${firstItem.label}" page.`,
          firstItem.description ? `${firstItem.description}.` : "",
          `Ask about tasks, summaries, or next steps for "${firstItem.label}".`,
        ].filter(Boolean).join(" ")

        return {
          id: messageId,
          role: "assistant",
          content: responseContent,
          timestamp: new Date(),
          scope,
          actions: [
            createNavigateAction("Open Page", `${isDemo ? "/demo" : ""}${firstItem.value}`),
            createRatingAction(messageId),
            createCopyAction(responseContent),
          ],
        }
      }
    }

    const activeDeals = demoDeals.filter((deal) => deal.status === "In Progress")
    const totalDealValue = demoDeals.reduce((sum, deal) => sum + parseCurrencyValue(deal.value), 0)
    const averageDealValue = demoDeals.length > 0 ? Math.round(totalDealValue / demoDeals.length) : 0

    const genericResponses: Array<{ content: string; actions: Message["actions"] }> = [
      {
        content: `I found ${demoContractsData.length} contracts in your vault. "${demoContractsData[0]?.name ?? "your latest contract"}" is a good place to start if you want clause-level analysis.`,
        actions: demoContractIds.ARTIST_RECORDING_DEAL
          ? [
              createNavigateAction("View Contract", `${paths.analysis}/${demoContractIds.ARTIST_RECORDING_DEAL}`),
              createNavigateAction("Open Vault", paths.vault),
            ]
          : undefined,
      },
      {
        content: `Based on your ${demoDeals.length} demo deals, the average deal value is $${averageDealValue.toLocaleString()}. You currently have ${activeDeals.length} active deals in progress.`,
        actions: [
          createNavigateAction("Open Dashboard", paths.dashboard),
          createNavigateAction("Deal Pipeline", paths.dealPipeline),
        ],
      },
      {
        content: `You can ask me to summarize a contract, compare two deals, or walk through deliverables. "${userInput}" is a good start, and I can get more specific if you add context.`,
        actions: [
          createNavigateAction("Open Chat", paths.chat),
        ],
      },
    ]

    const selectedResponse = genericResponses[Math.floor(Math.random() * genericResponses.length)]
    return {
      id: messageId,
      role: "assistant",
      content: selectedResponse.content,
      timestamp: new Date(),
      scope,
      actions: [
        ...(selectedResponse.actions ?? []),
        createRatingAction(messageId),
        createCopyAction(selectedResponse.content),
      ],
    }
  }, [demoContractIds, demoContractsData, demoDeals, isDemo])

  const sendMessage = useCallback((overrideText?: string, options?: { scope?: ChatScope }) => {
    if (!currentSession) {
      return
    }

    const content = (overrideText ?? currentSession.draft).trim()
    if (!content || loadingSessionIds.includes(currentSession.id)) {
      return
    }

    const sessionId = currentSession.id
    const userMessageId = createSessionId()
    const assistantMessageId = createSessionId()
    const contextItems = currentSession.contextItems.map(normaliseContextItem)
    const scope = options?.scope ?? currentSession.scope

    const userMessage: Message = {
      id: userMessageId,
      role: "user",
      content,
      timestamp: new Date(),
      scope,
      contextItems,
    }

    openPanel()
    updateSession(sessionId, (session) => {
      const nextMessages = [...session.messages, userMessage]
      return {
        ...session,
        title: session.messages.length === 0
          ? content.slice(0, 50) + (content.length > 50 ? "..." : "")
          : session.title,
        summary: generateSessionSummary({
          ...session,
          messages: nextMessages,
        }),
        messages: nextMessages,
        scope,
        draft: "",
        updatedAt: new Date(),
      }
    })
    setLoadingSessionIds((currentIds) => [...currentIds, sessionId])

    // sendApi is the injection point for message dispatch. When provided,
    // it replaces both the demo mock and the real API — the caller controls
    // how responses arrive (always via socketHook → handleSseFrame).
    if (sendApi) {
      void (async () => {
        try {
          await sendApi({
            chatSessionId: sessionId,
            bodyText: content,
            contextItems: contextItems.map((c) => ({
              kind: c.type,
              id: c.id,
              label: c.label,
            })),
            scope: typeof scope === "string" ? scope : undefined,
          })
        } catch (err) {
          console.error("[ChatPanelContext] sendApi failed", err)
          const detail = err instanceof Error ? err.message : "Unknown error"
          toast.error(`Couldn't send message: ${detail}`)
          setLoadingSessionIds((ids) => ids.filter((id) => id !== sessionId))
        }
      })()
      return
    }

    // Demo mode: generate response locally from mock data
    if (isDemo) {
      window.setTimeout(() => {
        const assistantMessage = buildMockResponse(content, assistantMessageId, scope, contextItems)
        updateSession(sessionId, (session) => ({
          ...session,
          summary: generateSessionSummary(session),
          messages: [...session.messages, assistantMessage],
          updatedAt: new Date(),
        }))
        setLoadingSessionIds((currentIds) => currentIds.filter((id) => id !== sessionId))
      }, 900)
      return
    }

    // Production: POST to backend, responses arrive via SSE
    void (async () => {
      try {
        await sendChatMessageApi({
          chatSessionId: sessionId,
          bodyText: content,
          contextItems: contextItems.map((c) => ({
            kind: c.type,
            id: c.id,
            label: c.label,
          })),
          scope: typeof scope === "string" ? scope : undefined,
        })
      } catch (err) {
        console.error("[ChatPanelContext] sendChatMessageApi failed", err)
        const detail = err instanceof Error ? err.message : "Unknown error"
        toast.error(`Couldn't send message: ${detail}`)
        setLoadingSessionIds((ids) => ids.filter((id) => id !== sessionId))
        return
      }

      setTimeout(() => {
        setLoadingSessionIds((ids) => {
          if (!ids.includes(sessionId)) return ids
          console.warn("[ChatPanelContext] chat reply timed out after 60s")
          toast.error("Ari is taking longer than expected to respond. Please try sending your message again.")
          return ids.filter((id) => id !== sessionId)
        })
      }, 60_000)
    })()
  }, [buildMockResponse, currentSession, isDemo, loadingSessionIds, openPanel, sendApi, updateSession])

  const value: ChatPanelContextType | undefined = currentSession
    ? {
        sessions,
        currentSessionId,
        currentSession,
        messages: currentSession.messages,
        isOpen,
        setIsOpen: setPanelOpen,
        openPanel,
        closePanel,
        togglePanel,
        isCurrentSessionLoading: loadingSessionIds.includes(currentSession.id),
        settings,
        updateSettings,
        applyPreset,
        layoutMode,
        setLayoutMode,
        dockAnchor,
        setDockAnchor,
        panelSize,
        setPanelSize,
        togglePanelSize,
        currentDraft: currentSession.draft,
        setCurrentDraft,
        currentScope: currentSession.scope,
        setCurrentScope,
        contextItems: currentSession.contextItems,
        setContextItems,
        removeContextItem,
        dynamicPageItems,
        createSession,
        selectSession,
        deleteSession,
        sendMessage,
        progressFrames,
      }
    : undefined

  if (!value) {
    return null
  }

  return (
    <ChatPanelContext.Provider value={value}>
      {children}
    </ChatPanelContext.Provider>
  )
}
